/**
 * DealsApiService — API-first deals operations.
 */

import { ApiClient } from '../ApiClient.js';

export class DealsApiService {
    static async list({ page = 1, per_page = 50 } = {}) {
        return ApiClient.get('/api/v1/deals', { page, per_page });
    }

    /** Table/Kanban projection — display names, no child graph. Returns { data, meta }. */
    static async listView({ page = 1, per_page = 50, cursor = null, updated_since = null } = {}) {
        const params = { per_page };
        if (cursor) {
            params.cursor = cursor;
        } else {
            params.page = page;
        }
        if (updated_since) params.updated_since = updated_since;
        return ApiClient.getWithMeta('/api/v1/deals/list-view', { params });
    }

    static async getById(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}`);
    }

    static async getBundle(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/bundle`);
    }

    static async create(payload) {
        return ApiClient.post('/api/v1/deals', payload);
    }

    static async update(dealId, payload) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}`, payload);
    }

    static async delete(dealId) {
        return ApiClient.delete(`/api/v1/deals/${encodeURIComponent(dealId)}`);
    }

    // Billing Details
    static async getBillingDetails(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/billing-details`);
    }

    static async upsertBillingDetails(dealId, payload) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}/billing-details`, payload);
    }

    // Milestones
    static async getMilestones(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/milestones`);
    }

    static async replaceMilestones(dealId, milestones) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}/milestones`, milestones);
    }

    // Custom Schedules
    static async getCustomSchedules(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/custom-schedules`);
    }

    static async replaceCustomSchedules(dealId, schedules) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}/custom-schedules`, schedules);
    }

    // Contact Roles
    static async getContactRoles(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/contact-roles`);
    }

    static async replaceContactRoles(dealId, roles) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}/contact-roles`, roles);
    }

    // Commission Splits
    static async getCommissionSplits(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/commission-splits`);
    }

    /** Batch schedules for many deals — GET /deals/schedules?deal_ids=... */
    static async getSchedulesBatch(dealIds = []) {
        const ids = [...new Set((dealIds || []).filter(Boolean))];
        if (!ids.length) return {};
        return ApiClient.get('/api/v1/deals/schedules', {
            deal_ids: ids,
        });
    }

    /** Pending next-step / task / comment counts — GET /deals/insight-counts?ids=... */
    static async getInsightCounts(dealIds = []) {
        const ids = [...new Set((dealIds || []).filter(Boolean))];
        if (!ids.length) return {};

        // Chunk so the query string stays under IIS maxQueryString (2048).
        const CHUNK_SIZE = 40;
        const merged = {};
        for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
            const part = await ApiClient.get('/api/v1/deals/insight-counts', {
                ids: ids.slice(i, i + CHUNK_SIZE),
            });
            if (part && typeof part === 'object') {
                Object.assign(merged, part);
            }
        }
        return merged;
    }

    static async replaceCommissionSplits(dealId, splits) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}/commission-splits`, splits);
    }

    // Commission Config (model / rate / fixed amount / eligibility)
    static async getCommissionConfig(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/commission-config`);
    }

    static async upsertCommissionConfig(dealId, payload) {
        return ApiClient.put(`/api/v1/deals/${encodeURIComponent(dealId)}/commission-config`, payload);
    }

    // Next Steps
    static async getNextSteps(dealId) {
        return ApiClient.get(`/api/v1/deals/${encodeURIComponent(dealId)}/next-steps`);
    }

    static async createNextStep(dealId, payload) {
        return ApiClient.post(`/api/v1/deals/${encodeURIComponent(dealId)}/next-steps`, payload);
    }

    static async updateNextStep(dealId, stepId, payload) {
        return ApiClient.patch(`/api/v1/deals/${encodeURIComponent(dealId)}/next-steps/${encodeURIComponent(stepId)}`, payload);
    }

    static async deleteNextStep(dealId, stepId) {
        return ApiClient.delete(`/api/v1/deals/${encodeURIComponent(dealId)}/next-steps/${encodeURIComponent(stepId)}`);
    }
}
